import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import "./Carousel.css";

const DIFFERENTIATORS = [
  {
    eyebrow: "Senior-led delivery",
    title: "A strong technical foundation from day one",
    description:
      "Our senior engineering strike team establishes the architecture, development environment, quality standards, and delivery processes at the beginning of your project. Once the foundation is stable, we shape a cost-effective team around your roadmap while senior specialists remain available for oversight.",
    image: "/images/quality.jpg",
    highlights: ["Architecture review", "Delivery standards", "Senior oversight"],
  },
  {
    eyebrow: "Experience intelligence",
    title: "UX decisions backed by evidence",
    description:
      "Our UX audit identifies friction, unclear user journeys, accessibility gaps, and design inconsistencies before they become expensive development problems. You receive practical recommendations that improve usability, reduce rework, and strengthen long-term product ROI.",
    image: "/images/uiAudit.jpg",
    highlights: ["Journey analysis", "Usability review", "Actionable roadmap"],
  },
  {
    eyebrow: "Security by design",
    title: "Practical safeguards throughout delivery",
    description:
      "Security, access control, data handling, and delivery governance are considered from the start—not added at the end. When required, clearly defined milestones and independent escrow arrangements can provide additional commercial confidence for both parties.",
    image: "/images/security.jpg",
    highlights: ["Secure architecture", "Controlled access", "Clear governance"],
  },
  {
    eyebrow: "Committed partnership",
    title: "A stable team that understands your product",
    description:
      "You work with engaged engineers who build long-term product knowledge and take ownership of outcomes. Regular delivery reviews, transparent communication, and a healthy team culture help reduce disruption and keep progress predictable.",
    image: "/images/motivated.jpg",
    highlights: ["Product ownership", "Transparent reviews", "Team continuity"],
  },
];

const useReducedMotion = () => {
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduceMotion(query.matches);

    update();
    query.addEventListener?.("change", update);

    return () => query.removeEventListener?.("change", update);
  }, []);

  return reduceMotion;
};

const CarouselImage = ({ src, alt, sequence }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [src]);

  if (!src || failed) {
    return (
      <div className="difference-carousel__image-fallback" aria-hidden="true">
        AX / {String(sequence).padStart(2, "0")}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className="difference-carousel__image"
      loading={sequence === 1 ? "eager" : "lazy"}
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
};

const CardSlider = ({ id = "axiomos-difference", items = DIFFERENTIATORS }) => {
  const cards = Array.isArray(items) && items.length ? items : DIFFERENTIATORS;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef(null);
  const reduceMotion = useReducedMotion();
  const hasMultipleCards = cards.length > 1;

  useEffect(() => {
    if (currentIndex >= cards.length) setCurrentIndex(0);
  }, [cards.length, currentIndex]);

  useEffect(() => {
    if (!hasMultipleCards || isPaused || reduceMotion) return undefined;

    const timer = window.setTimeout(() => {
      setCurrentIndex((index) => (index + 1) % cards.length);
    }, 7000);

    return () => window.clearTimeout(timer);
  }, [cards.length, currentIndex, hasMultipleCards, isPaused, reduceMotion]);

  const goToCard = (index) => setCurrentIndex(index);
  const goToPrevious = () =>
    setCurrentIndex((index) => (index === 0 ? cards.length - 1 : index - 1));
  const goToNext = () =>
    setCurrentIndex((index) => (index + 1) % cards.length);

  const handleKeyDown = (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      goToPrevious();
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      goToNext();
    }
  };

  const handleTouchStart = (event) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event) => {
    if (touchStartX.current === null) return;

    const endX = event.changedTouches[0]?.clientX ?? touchStartX.current;
    const distance = touchStartX.current - endX;

    if (Math.abs(distance) > 48) {
      if (distance > 0) goToNext();
      else goToPrevious();
    }

    touchStartX.current = null;
  };

  const activeCard = cards[currentIndex];
  const highlights = Array.isArray(activeCard.highlights)
    ? activeCard.highlights.slice(0, 3)
    : [];
  const progress = ((currentIndex + 1) / cards.length) * 100;

  return (
    <section
      id={id}
      className="difference-carousel"
      aria-labelledby={`${id}-title`}
    >
      <div className="difference-carousel__decoration" aria-hidden="true" />

      <div className="difference-carousel__container">
        <header className="difference-carousel__header">
          <div>
            <p className="difference-carousel__section-eyebrow">
              <span aria-hidden="true" />
              The Axiomos difference
            </p>
            <h2 id={`${id}-title`}>What makes our partnership different.</h2>
          </div>

          <p className="difference-carousel__intro">
            A delivery model designed to protect quality, reduce unnecessary
            risk, and create lasting value from your technology investment.
          </p>
        </header>

        <div
          className="difference-carousel__stage"
          role="region"
          aria-roledescription="carousel"
          aria-label="Axiomos differentiators"
          tabIndex="0"
          onKeyDown={handleKeyDown}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onFocusCapture={() => setIsPaused(true)}
          onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              setIsPaused(false);
            }
          }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <article
            key={currentIndex}
            className="difference-carousel__card"
            aria-live="polite"
            aria-label={`Slide ${currentIndex + 1} of ${cards.length}`}
          >
            <div className="difference-carousel__media">
              <CarouselImage
                src={activeCard.image}
                alt={activeCard.title}
                sequence={currentIndex + 1}
              />
              <div className="difference-carousel__media-overlay" aria-hidden="true" />

              <span className="difference-carousel__sequence" aria-hidden="true">
                Difference / {String(currentIndex + 1).padStart(2, "0")}
              </span>

              <p className="difference-carousel__media-statement">
                Built for dependable outcomes.
              </p>
            </div>

            <div className="difference-carousel__content">
              <p className="difference-carousel__card-eyebrow">
                {activeCard.eyebrow || "Axiomos advantage"}
              </p>

              <h3>{activeCard.title}</h3>
              <p className="difference-carousel__description">
                {activeCard.description}
              </p>

              {highlights.length > 0 && (
                <ul
                  className="difference-carousel__highlights"
                  aria-label="Key benefits"
                >
                  {highlights.map((highlight) => (
                    <li key={highlight}>
                      <span aria-hidden="true">
                        <FontAwesomeIcon icon={faCheck} />
                      </span>
                      {highlight}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </article>

          {hasMultipleCards && (
            <div className="difference-carousel__controls">
              <div className="difference-carousel__arrows">
                <button
                  type="button"
                  onClick={goToPrevious}
                  aria-label="Show previous difference"
                >
                  <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={goToNext}
                  aria-label="Show next difference"
                >
                  <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                </button>
              </div>

              <div className="difference-carousel__pagination">
                {cards.map((card, index) => (
                  <button
                    key={card.title || index}
                    type="button"
                    className={index === currentIndex ? "is-active" : ""}
                    onClick={() => goToCard(index)}
                    aria-label={`Go to slide ${index + 1}: ${card.title}`}
                    aria-current={index === currentIndex ? "true" : undefined}
                  >
                    <span />
                  </button>
                ))}
              </div>

              <p className="difference-carousel__count" aria-hidden="true">
                <strong>{String(currentIndex + 1).padStart(2, "0")}</strong>
                <span>/</span>
                {String(cards.length).padStart(2, "0")}
              </p>
            </div>
          )}

          <div
            className="difference-carousel__progress"
            style={{ "--difference-progress": `${progress}%` }}
            aria-hidden="true"
          >
            <span />
          </div>
        </div>
      </div>
    </section>
  );
};

export default CardSlider;
